import { useEffect, useReducer, useCallback } from 'react';

import { useCurrentProject } from '@adsk/alloy-provider';

import { CommonReferencePickerPlugin } from '../types/ReferencePickerPlugin';
import {
  QueryEntitiesOptions,
  PluginQuerySort,
  PluginQueryFilters,
  AddEntitiesToCacheProps,
  EntityWithId,
} from '../types/types';

enum ACTIONS {
  SET_ENTITY_TYPE = 'SET_ENTITY_TYPE',
  SEARCH_CHANGE = 'SEARCH_CHANGE',
  FETCH_INIT = 'FETCH_INIT',
  FETCH_SUCCESS = 'FETCH_SUCCESS',
  FETCH_FAILURE = 'FETCH_FAILURE',
  FETCH_CLEAR = 'FETCH_CLEAR',
  SORT_CHANGED = 'SORT_CHANGED',
  CLEAR_CACHE = 'CLEAR_CACHE',
}

interface setEntityTypeAction {
  type: ACTIONS.SET_ENTITY_TYPE;
}

interface clearCacheAction {
  type: ACTIONS.CLEAR_CACHE;
}

interface searchChangeAction {
  type: ACTIONS.SEARCH_CHANGE;
  payload: PluginQueryFilters;
}

interface fetchInitAction {
  type: ACTIONS.FETCH_INIT;
  payload: {
    filters: PluginQueryFilters;
    sort: PluginQuerySort;
  };
}

interface fetchSuccessAction<T> {
  type: ACTIONS.FETCH_SUCCESS;
  payload: {
    filters: PluginQueryFilters;
    sort: PluginQuerySort;
    entities: T[];
    onLoadMore: (() => Promise<void>) | null;
  };
}

interface fetchFailureAction {
  type: ACTIONS.FETCH_FAILURE;
  payload: {
    filters: PluginQueryFilters;
    sort: PluginQuerySort;
    error: object;
    onTryAgain: () => void;
  };
}

interface fetchClearAction {
  type: ACTIONS.FETCH_CLEAR;
  payload: {
    filters: PluginQueryFilters;
    sort: PluginQuerySort;
  };
}

interface sortChangedAction {
  type: ACTIONS.SORT_CHANGED;
  payload: QueryEntitiesOptions['sort'];
}

type Action<T> =
  | setEntityTypeAction
  | clearCacheAction
  | searchChangeAction
  | fetchInitAction
  | fetchSuccessAction<T>
  | fetchFailureAction
  | fetchClearAction
  | sortChangedAction;

interface SearchResult<T> {
  loading: boolean;
  error: boolean | object;
  onTryAgain: (() => void) | null;
  entities: T[];
  onLoadMore: (() => Promise<void>) | null;
}

interface State<T> {
  filters: PluginQueryFilters;
  resultsBySearch: Record<string, SearchResult<T>>;
  sort: PluginQuerySort;
}

const reduceToString = (obj: Record<string, unknown>): string => {
  return Object.keys(obj)
    .sort()
    .map((key) => `${key}=${obj[key]}`)
    .join(',');
};

const getFiltersKey = (
  filters: PluginQueryFilters,
  sort: PluginQuerySort,
): string => {
  return reduceToString(filters) + reduceToString(sort);
};

const dataReducer = <T extends EntityWithId>(
  state: State<T>,
  action: Action<T>,
): State<T> => {
  switch (action.type) {
    case ACTIONS.CLEAR_CACHE:
    case ACTIONS.SET_ENTITY_TYPE:
      return {
        ...state,
        resultsBySearch: {},
      };
    case ACTIONS.SEARCH_CHANGE:
      return {
        ...state,
        filters: action.payload,
      };
    case ACTIONS.FETCH_INIT: {
      const { filters = {}, sort = {} } = action.payload;
      const filtersKey = getFiltersKey(filters, sort);
      return {
        ...state,
        resultsBySearch: {
          ...state.resultsBySearch,
          [filtersKey]: {
            ...(state.resultsBySearch[filtersKey] || {}),
            loading: true,
            error: false,
            onTryAgain: null,
          },
        },
      };
    }
    case ACTIONS.FETCH_SUCCESS: {
      const { filters = {}, sort = {} } = action.payload;
      const filtersKey = getFiltersKey(filters, sort);
      return {
        ...state,
        resultsBySearch: {
          ...state.resultsBySearch,
          [filtersKey]: {
            ...(state.resultsBySearch[filtersKey] || {}),
            loading: false,
            error: false,
            onTryAgain: null,
            entities: (
              (state.resultsBySearch[filtersKey] &&
                state.resultsBySearch[filtersKey].entities) ||
              []
            ).concat(action.payload.entities),
            onLoadMore: action.payload.onLoadMore,
          },
        },
      };
    }
    case ACTIONS.FETCH_FAILURE: {
      const { filters = {}, sort = {} } = action.payload;
      const filtersKey = getFiltersKey(filters, sort);
      return {
        ...state,
        resultsBySearch: {
          ...state.resultsBySearch,
          [filtersKey]: {
            ...state.resultsBySearch[filtersKey],
            loading: false,
            error: action.payload.error,
            onTryAgain: action.payload.onTryAgain,
          },
        },
      };
    }
    case ACTIONS.FETCH_CLEAR: {
      const { filters = {}, sort = {} } = action.payload;
      const filtersKey = getFiltersKey(filters, sort);
      const newState = { ...state };
      delete newState.resultsBySearch[filtersKey];
      return newState;
    }
    case ACTIONS.SORT_CHANGED:
      return {
        ...state,
        sort: action.payload || {},
      };
    default:
      throw new Error();
  }
};

interface UseEntitySearchProps {
  plugin?: CommonReferencePickerPlugin | null;
  pageLimit?: number;
  delay?: number;
  addEntitiesToCache?: (params: AddEntitiesToCacheProps) => void;
}

interface UseEntitySearchReturnType<T> {
  entities: T[];
  loading: boolean;
  error: boolean | object;
  onTryAgain: (() => void) | null;
  onLoadMore: (() => Promise<void>) | null;
  filters: PluginQueryFilters;
  setFilters: (filters: PluginQueryFilters) => void;
  sort: QueryEntitiesOptions['sort'];
  setSort?: (sortOptions: QueryEntitiesOptions['sort']) => void;
  clearCache: () => void;
}

/**
 * This hook returns data for searched entities, using given plugin to fetch the relevant data.
 * Returned value:
 * entities - the list of entities retrieved for current search expression.
 * loading - whether it currently loads data from BE.
 * error - whether fetching data raised an error.
 * onLoadMore - callback to load more entities.
 * onTryAgain - callback to re-fetch data from BE.
 * filters - the current filters.
 * setFilters - callback to set the current filter.
 *
 * @param plugin
 * @param pageLimit
 * @param delay
 * @param addEntitiesToCache
 * @returns {{loading: *, onLoadMore: *, filters: *, onTryAgain: *, entities: *, setFilters: *, error: *}}
 */
const useEntitySearch = <T extends EntityWithId>({
  plugin,
  pageLimit = 10,
  delay = 500,
  addEntitiesToCache,
}: UseEntitySearchProps): UseEntitySearchReturnType<T> => {
  const [state, dispatch] = useReducer(
    dataReducer<T>,
    plugin,
    (initialPlugin): State<T> => ({
      // One-way seed only: plugin changes after mount must not reset user-edited filters.
      filters: initialPlugin?.initialFilters ?? {},
      resultsBySearch: {},
      sort: {},
    }),
  );
  const currentProject = useCurrentProject();
  const projectId = currentProject?.id;
  const projectScope = currentProject?.scopes?.project;

  const { filters, resultsBySearch, sort } = state;
  const filtersKey = getFiltersKey(filters, sort);
  const {
    entities = [],
    loading = true,
    error,
    onTryAgain,
    onLoadMore,
  } = resultsBySearch[filtersKey] || {};
  const type = plugin?.metadata?.type;
  const domain = plugin?.metadata?.domain;
  useEffect(() => {
    dispatch({ type: ACTIONS.SET_ENTITY_TYPE });
  }, [type, domain]);

  useEffect(() => {
    let handler: NodeJS.Timeout | undefined,
      unmounted = false;
    const queryEntities = plugin?.queryEntities;
    if (plugin && projectId && !resultsBySearch[filtersKey] && queryEntities) {
      handler = setTimeout(() => {
        const readData = (query: QueryEntitiesOptions) => {
          dispatch({ type: ACTIONS.FETCH_INIT, payload: { filters, sort } });
          return queryEntities(query)
            .then(
              ({
                data,
                nextPageQuery,
              }: {
                data: T[];
                nextPageQuery?: QueryEntitiesOptions;
              }) => {
                if (addEntitiesToCache && domain && type) {
                  addEntitiesToCache({
                    entities: data,
                    domain,
                    type,
                  });
                }
                dispatch({
                  type: ACTIONS.FETCH_SUCCESS,
                  payload: {
                    filters,
                    sort,
                    entities: data,
                    onLoadMore: nextPageQuery
                      ? () => readData(nextPageQuery)
                      : null,
                  },
                });
              },
            )
            .catch((e) => {
              console.error(
                'ReferencePicker - useEntitySearch: Error searching entities',
                e,
              );
              if (unmounted) {
                dispatch({
                  type: ACTIONS.FETCH_CLEAR,
                  payload: {
                    filters,
                    sort,
                  },
                });
              } else {
                dispatch({
                  type: ACTIONS.FETCH_FAILURE,
                  payload: {
                    filters,
                    sort,
                    error: e,
                    onTryAgain: () => readData(query),
                  },
                });
              }
            });
        };
        readData({ filters, pageLimit, projectId, projectScope, sort });
      }, delay);
    }
    return () => {
      unmounted = true;
      if (handler) {
        clearTimeout(handler);
      }
    };
    // Disable as we don't want to refetch when `resultsBySearch` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plugin, filters, projectId, projectScope, delay, sort]);

  const setFilters = useCallback((newFilters: PluginQueryFilters) => {
    dispatch({ type: ACTIONS.SEARCH_CHANGE, payload: newFilters });
  }, []);

  const setSort = useCallback((sortOptions: QueryEntitiesOptions['sort']) => {
    dispatch({ type: ACTIONS.SORT_CHANGED, payload: sortOptions });
  }, []);

  const clearCache = useCallback(() => {
    dispatch({ type: ACTIONS.CLEAR_CACHE });
  }, []);

  return {
    entities,
    loading,
    error,
    onTryAgain,
    onLoadMore,
    filters,
    setFilters,
    sort,
    setSort,
    clearCache,
  };
};

export default useEntitySearch;
